import { forwardRef, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/Popover";
import { FilterListIcon, SearchIcon } from "../lib/icons";
import { cn } from "../lib/cn";

export interface PageToolbarProps {
  /** Chip-tab row — pass a `<Tabs>`/`<TabsList variant="boxed">` composition. */
  tabs?: ReactNode;
  /** Search input value — the input renders only when `onSearchChange` is provided. */
  searchValue?: string;
  /** Search change callback — presence of this prop enables the search input. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Content of the filter popover — presence of this prop enables the Filter trigger. */
  filter?: ReactNode;
  /** Marks the Filter trigger as holding active filters (accent colour). */
  filterActive?: boolean;
  /** Right-aligned action nodes. */
  actions?: ReactNode;
  className?: string;
}

/**
 * Page furniture — the recurring toolbar above ledgers.
 *
 *   <PageToolbar
 *     tabs={<TabsList variant="boxed">…</TabsList>}
 *     searchValue={query}
 *     onSearchChange={setQuery}
 *     filter={<FilterFields … />}
 *     actions={<Button size="sm">Export</Button>}
 *   />
 *
 * Search is controlled straight through to the Input; the filter is a
 * Popover whose trigger the toolbar owns. Everything is optional — each
 * slot appears only when its props are present.
 */
export const PageToolbar = forwardRef<HTMLDivElement, PageToolbarProps>(
  (
    { tabs, searchValue, onSearchChange, searchPlaceholder, filter, filterActive, actions, className },
    ref,
  ) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const hasSearch = onSearchChange !== undefined;

    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-(--control-height) flex-wrap items-center gap-2 border-b border-border-default px-4 py-1 sm:px-6",
          className,
        )}
      >
        {tabs ? <div className="min-w-0 flex-1 basis-40">{tabs}</div> : null}
        {hasSearch ? (
          <Input
            type="search"
            inputSize="sm"
            className="w-full sm:w-56"
            placeholder={searchPlaceholder ?? "Search…"}
            aria-label={searchPlaceholder ?? "Search"}
            value={searchValue}
            onChange={(event) => {
              onSearchChange?.(event.target.value);
            }}
            startAdornment={
              <SearchIcon className="size-4 text-content-secondary" aria-hidden="true" />
            }
          />
        ) : null}
        {filter ? (
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className={cn(filterActive && "text-content-accent")}
                aria-label="Filters"
              >
                <FilterListIcon className="size-4" aria-hidden="true" />
                Filter
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72">
              {filter}
            </PopoverContent>
          </Popover>
        ) : null}
        {actions ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
    );
  },
);
PageToolbar.displayName = "PageToolbar";
